import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Hook to detect text overflow and truncate with ellipsis.
 * Works reliably with text-right alignment (unlike CSS line-clamp).
 */
export const useTextTruncation = (text: string, maxLines: number) => {
  const measureRef = useRef<HTMLSpanElement>(null);
  const [displayText, setDisplayText] = useState(text);

  const truncate = useCallback(() => {
    const el = measureRef.current;
    if (!el) return;

    // Reset to full text for measurement
    el.textContent = text;

    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
    const maxHeight = lineHeight * maxLines;

    // If text fits, no truncation needed
    if (el.scrollHeight <= maxHeight + 1) {
      setDisplayText(text);
      return;
    }

    // Binary search for the right truncation point
    let low = 0;
    let high = text.length;

    while (low < high) {
      const mid = Math.ceil((low + high) / 2);
      el.textContent = text.substring(0, mid) + '…';

      if (el.scrollHeight > maxHeight + 1) {
        high = mid - 1;
      } else {
        low = mid;
      }
    }

    setDisplayText(text.substring(0, low).trimEnd() + '…');
    // Restore final text
    el.textContent = text.substring(0, low).trimEnd() + '…';
  }, [text, maxLines]);

  useEffect(() => {
    truncate();

    const observer = new ResizeObserver(() => truncate());
    if (measureRef.current?.parentElement) {
      observer.observe(measureRef.current.parentElement);
    }
    return () => observer.disconnect();
  }, [truncate]);

  return { measureRef, displayText };
};
